import { is } from "immutable";
import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";

// Component that helps to restore scroll position on browser back/forward buttons
// https://github.com/ipatate/react-router-scroll-memory
// Modified to work with hashes in url

interface PageInfo { pathname: string; scroll: number; }

/**
 * search object and return object find or undefined
 * @param {array} url
 * @param {string} path
 * @return {object}
 */
const getPage = (url: PageInfo[] = [], path: string): PageInfo | undefined => {
    const lastIndex = url.map((x) => x.pathname).indexOf(path);
    return lastIndex > -1 ? url[lastIndex] : undefined;
};

/**
 * search object and return index or -1
 * @param {array} url
 * @param {string} path
 * @return {number}
 */
const getIndexPage = (url: PageInfo[] = [], path: string): number => url.map((x) => x.pathname).indexOf(path);

/**
 * save path and scroll in url array
 * @param {array} url
 * @param {string} pathname
 * @param {number} scroll
 * @return {array}
 */
const saveUrl = (url: PageInfo[], pathname: string = "", scroll: number = 0): PageInfo[] => {
    const u = Array.isArray(url) ? url : [];
    // if path exist in array replace scroll value
    const page = getIndexPage(u, pathname);
    if (page > -1) {
        u[page].scroll = scroll;
        return u;
    }
    // save actual url
    u.push({ pathname, scroll });
    return u;
};

/**
 * get the top position of element
 * @param {string} elementId
 * @param {boolean} [relativeToDoc]
 */
const getElementTop = (elementId: string, wrapperId?: string, relativeToDoc: boolean = true): number | undefined => {
    const element = document.getElementById(elementId);
    let wrapper: HTMLElement | null = null;
    if (wrapperId)
        wrapper = document.getElementById(wrapperId);

    if (element !== null) {
        const pos = element.getBoundingClientRect();

        if (!relativeToDoc)
            return pos.top;

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        if (wrapper) {
            const wrapperPos = wrapper.getBoundingClientRect();
            return pos.top + scrollTop - wrapperPos.top + wrapper.scrollTop;
        }

        return pos.top + scrollTop;
    }

    return undefined;
};

/**
 * get the scroll of page
 * @return {number}
 */
const getScrollPage = (): number => {
    let docScrollTop = 0;
    if (document.documentElement && document.documentElement !== null) {
        docScrollTop = document.documentElement.scrollTop;
    }
    return window.pageYOffset || docScrollTop;
};

/**
 * get the scroll of a parent element
 * @return {number}
 */
const getScrollElement = (elementId: string): number | undefined => {
    let elemScrollTop: number | undefined;
    const element = document.getElementById(elementId);
    if (element !== null) {
        elemScrollTop = element.scrollTop;
    }
    return elemScrollTop;
};

/**
 * scroll to y number of a page
 * @param {number} scrollnumber
 * @return {void}
 */
const scrollTo = (scrollnumber: number = 0): number =>
    window.requestAnimationFrame(() => {
        window.scrollTo(0, scrollnumber);
    });

/**
 * scroll to y number inside an element
 * @param {number} scrollnumber
 * @return {void}
 */
const scrollToElement = (scrollnumber: number = 0, elementId: string): number => {
    const element = document.getElementById(elementId);
    if (element) {
        // element.scroll({
        //     top: scrollnumber,
        // });

        window.requestAnimationFrame(() => {
            element.scroll({
                top: scrollnumber,
            });
        });
    }
    return 0;
};

/**
 * verif if window exist
 * @return boolean
 */
const isBrowser = (): boolean => typeof window !== "undefined";

type OwnProps = RouteComponentProps<{}> & {
    elementID?: string;
};

interface OwnState {
}

class ScrollRestoreComponent extends React.Component<OwnProps, OwnState> {

    // private detectPop: () => void;
    private requestID: number;
    private url: PageInfo[];

    constructor(props: OwnProps) {
        super(props);
        this.detectPop = this.detectPop.bind(this);
        // stock all pathname with scroll associate
        this.url = [];
        this.requestID = 0;
    }

    public componentDidMount(): void {
        if (!isBrowser()) return;
        window.addEventListener("popstate", this.detectPop);
    }

    public componentWillReceiveProps(nextProps: OwnProps): void {
        if (!isBrowser()) return;

        // actual is previous location before click on link
        const actual = this.props.location;

        // next is target location of the link
        const next = nextProps.location;
        // determined by (pathname + hash) of actual and next location
        const locationChanged = !is(actual.pathname + actual.hash, next.pathname + next.hash); // next !== actual;

        let scroll: number | undefined;
        let hash: string | undefined;
        const hasHash = next.hash && next.hash.length > 1;
        let goToTop = 0;

        // if hash is given in URL, get scroll of that element
        if (hasHash) {
            hash = next.hash.substr(1);
            goToTop = getElementTop(hash, this.props.elementID) || 0;
        }

        // get scroll of the page or the element before change location
        scroll = this.props.elementID ? getScrollElement(this.props.elementID) : getScrollPage();

        if (locationChanged) {
            this.requestID = this.props.elementID ? scrollToElement(goToTop, this.props.elementID) : scrollTo(goToTop);
            this.url = saveUrl(this.url, actual.pathname + actual.hash, scroll);
        }
    }

    public componentWillUnmount(): void {
        if (!isBrowser()) return;
        window.removeEventListener("popstate", this.detectPop);
    }

    public render(): null {
        return null;
    }

    /**
     * callback for event popstate
     *
     * @memberof ScrollMemory
     */
    private detectPop(): void {
        if (!isBrowser()) return;
        const pathname = window.location.pathname + window.location.hash;
        const nextFind = getPage(this.url, pathname);

        if (this.requestID > 0) {
            // cancel scrollTo call by componentWillReceiveProps
            window.cancelAnimationFrame(this.requestID);
            this.requestID = 0;
        }
        if (nextFind) {
            let goToTop: number | undefined;
            if (window.location.hash && window.location.hash.length > 1) {
                goToTop = getElementTop(window.location.hash.substr(1), this.props.elementID);
            }

            this.props.elementID
                ? scrollToElement(goToTop !== undefined ? goToTop : nextFind.scroll, this.props.elementID)
                : scrollTo(goToTop !== undefined ? goToTop : nextFind.scroll);
        }
    }
}

export const ScrollRestore: React.ComponentClass<OwnProps> = withRouter(ScrollRestoreComponent) as any;



// WEBPACK FOOTER //
// ./src/components/page/ScrollRestore.tsx